import { v4 as uuidv4 } from "uuid";
import { stripFormattingTags } from "../../features/rich_text/RichTextHtmlUtils";
import Interaction from "./Interaction";
import {
  getPowerSlideTemplate,
  POWER_SLIDE_TITLE_FIELD,
  PowerSlideTemplate,
  PowerSlideTemplateId,
  PowerSlideTextField,
} from "./PowerSlideTemplates";
// PowerSlideTextField is used for the paired-field group below as well as for
// the item-count helpers.
import {
  DEFAULT_POWER_SLIDE_THEME,
  PowerSlideTheme,
} from "./PowerSlideTheme";

/**
 * A field holds either a single localization key or, for a repeating field, an
 * ordered list of them. Whether the list templates (Key Points, Numbered, By
 * the Numbers) are fixed-length or a real collection is still an open design
 * question; carrying both shapes from the start keeps that answer from turning
 * into a migration of saved trainings.
 */
export type PowerSlideFieldValue = string | string[];

export interface PowerSlideAssetRef {
  slotId: string;
  /**
   * Deliberately named assetId: the clipboard reference walker recognises asset
   * dependencies by property name, so a differently named field would not be
   * cleared when a deck is pasted into another training.
   */
  assetId: string;
}

/**
 * A slide is pure content. It carries no colour or appearance field - that
 * belongs to the template and the deck theme - so there is nothing to convert
 * when a slide moves between decks.
 */
export interface ISlide {
  id: string;
  templateId: PowerSlideTemplateId;
  /** Field id to localization key(s). */
  fields: Record<string, PowerSlideFieldValue>;
  assets: PowerSlideAssetRef[];
}

export interface IPowerSlide extends Interaction {
  slides: ISlide[];
  /**
   * The deck's own colour. Slides still carry no styling - a slide pasted into
   * another deck simply takes that deck's theme - but the deck is where the
   * theme lives, so one presentation can be retouched without repainting every
   * PowerSlide in the training.
   */
  theme: PowerSlideTheme;
}

/**
 * The theme a deck renders with. Required on the model, tolerant on read: a
 * deck typed by hand, or one written before the theme moved off the training,
 * renders as the default rather than colourless.
 */
export function resolvePowerSlideTheme(
  deck: IPowerSlide | undefined
): PowerSlideTheme {
  return deck?.theme ?? DEFAULT_POWER_SLIDE_THEME;
}

/** How many items a repeating field starts with, clamped to its own bounds. */
function initialItemCount(field: PowerSlideTextField): number {
  if (!field.repeating) return 1;
  const min = field.minItems ?? 0;
  const max = field.maxItems ?? Number.MAX_SAFE_INTEGER;
  return Math.min(Math.max(field.defaultItems ?? min, min), max);
}

/** How many localization keys a fresh slide of this template consumes. */
export function countPowerSlideTemplateKeys(
  templateId: PowerSlideTemplateId
): number {
  const template = getPowerSlideTemplate(templateId);
  if (!template) return 0;
  return template.textFields.reduce(
    (total, field) => total + initialItemCount(field),
    0
  );
}

/**
 * Builds a slide for a template, consuming keys in declaration order. Extra
 * keys are ignored so callers can allocate for a whole deck in one request; if
 * too few are supplied the remaining fields are left empty rather than sharing.
 */
export function createPowerSlide(
  templateId: PowerSlideTemplateId,
  keys: string[]
): ISlide {
  const template = getPowerSlideTemplate(templateId);
  const fields: Record<string, PowerSlideFieldValue> = {};
  let next = 0;
  template?.textFields.forEach((field) => {
    const count = initialItemCount(field);
    if (field.repeating) {
      fields[field.id] = Array.from(
        { length: count },
        () => keys[next++] ?? ""
      ).filter(Boolean);
    } else {
      fields[field.id] = keys[next++] ?? "";
    }
  });
  return { id: uuidv4(), templateId, fields, assets: [] };
}

/**
 * Switching a slide's template keeps any field the new template also declares,
 * so retyping a title is never the price of trying a different layout. Fields
 * the new template does not have are dropped; fields it adds get fresh keys.
 */
export function changePowerSlideTemplate(
  slide: ISlide,
  templateId: PowerSlideTemplateId,
  getKeys: (amount: number) => string[]
): ISlide {
  const template = getPowerSlideTemplate(templateId);
  if (!template || slide.templateId === templateId) return slide;

  // Work out the shortfall first so every new key comes from one allocation.
  const carried = new Map<string, string[]>();
  let needed = 0;
  template.textFields.forEach((field) => {
    const existing = getPowerSlideFieldKeys(slide, field.id);
    const wanted = field.repeating
      ? Math.min(
          Math.max(existing.length || initialItemCount(field), field.minItems ?? 0),
          field.maxItems ?? Number.MAX_SAFE_INTEGER
        )
      : 1;
    carried.set(field.id, existing.slice(0, wanted));
    needed += Math.max(0, wanted - Math.min(existing.length, wanted));
  });

  const fresh = getKeys(needed);
  let next = 0;
  const fields: Record<string, PowerSlideFieldValue> = {};
  template.textFields.forEach((field) => {
    const existing = carried.get(field.id) ?? [];
    const wanted = field.repeating
      ? Math.min(
          Math.max(existing.length || initialItemCount(field), field.minItems ?? 0),
          field.maxItems ?? Number.MAX_SAFE_INTEGER
        )
      : 1;
    const keys = Array.from(
      { length: wanted },
      (_unused, index) => existing[index] ?? fresh[next++] ?? ""
    );
    fields[field.id] = field.repeating ? keys.filter(Boolean) : keys[0];
  });

  return { ...slide, templateId, fields };
}

function repeatingField(
  slide: ISlide,
  fieldId: string
): PowerSlideTextField | undefined {
  const field = getPowerSlideTemplate(slide.templateId)?.textFields.find(
    (candidate) => candidate.id === fieldId
  );
  return field?.repeating ? field : undefined;
}

/**
 * The fields that must grow and shrink together, starting with the one asked
 * for. A field with no partner is a group of one, so every caller below reads
 * the same whether or not pairing is involved - there is no second code path
 * that could be reached with the wrong one.
 */
export function powerSlideItemGroup(
  slide: ISlide,
  fieldId: string
): PowerSlideTextField[] {
  const field = repeatingField(slide, fieldId);
  if (!field) return [];
  const partner = field.pairedWith
    ? repeatingField(slide, field.pairedWith)
    : undefined;
  return partner ? [field, partner] : [field];
}

export function canAddPowerSlideItem(slide: ISlide, fieldId: string): boolean {
  const group = powerSlideItemGroup(slide, fieldId);
  if (!group.length) return false;
  // Every member has to have room. A paired list that has drifted - from
  // hand-edited JSON, say - is repaired by removing, not by adding onto the
  // longer side.
  return group.every(
    (field) =>
      getPowerSlideFieldKeys(slide, field.id).length < (field.maxItems ?? 0)
  );
}

export function canRemovePowerSlideItem(
  slide: ISlide,
  fieldId: string
): boolean {
  const group = powerSlideItemGroup(slide, fieldId);
  if (!group.length) return false;
  return group.every(
    (field) =>
      getPowerSlideFieldKeys(slide, field.id).length > (field.minItems ?? 0)
  );
}

/**
 * Adds one item to the field and to anything paired with it. Keys come from an
 * allocator rather than a single string so a paired add makes one request for
 * both, which is also the only way to be sure the two keys differ.
 */
export function addPowerSlideItem(
  slide: ISlide,
  fieldId: string,
  getKeys: (amount: number) => string[]
): ISlide {
  const group = powerSlideItemGroup(slide, fieldId);
  if (!canAddPowerSlideItem(slide, fieldId)) return slide;
  const keys = getKeys(group.length);
  const fields = { ...slide.fields };
  group.forEach((field, position) => {
    fields[field.id] = [
      ...getPowerSlideFieldKeys(slide, field.id),
      keys[position] ?? "",
    ];
  });
  return { ...slide, fields };
}

/** Removes the item at an index from the field and from anything paired. */
export function removePowerSlideItem(
  slide: ISlide,
  fieldId: string,
  index: number
): ISlide {
  const group = powerSlideItemGroup(slide, fieldId);
  if (!canRemovePowerSlideItem(slide, fieldId)) return slide;
  const fields = { ...slide.fields };
  group.forEach((field) => {
    fields[field.id] = getPowerSlideFieldKeys(slide, field.id).filter(
      (_unused, position) => position !== index
    );
  });
  return { ...slide, fields };
}

export function getPowerSlideAssetId(slide: ISlide, slotId: string): string {
  return (
    (slide.assets ?? []).find((asset) => asset.slotId === slotId)?.assetId ?? ""
  );
}

/**
 * An empty slot is absent from the array rather than present with an empty
 * assetId, so a slide only ever records assets it actually has.
 */
export function setPowerSlideAsset(
  slide: ISlide,
  slotId: string,
  assetId: string
): ISlide {
  const others = (slide.assets ?? []).filter(
    (asset) => asset.slotId !== slotId
  );
  return {
    ...slide,
    assets: assetId ? [...others, { slotId, assetId }] : others,
  };
}

export function clearPowerSlideAsset(slide: ISlide, slotId: string): ISlide {
  return setPowerSlideAsset(slide, slotId, "");
}

/** Normalises a field to a list of keys, whether or not it repeats. */
export function getPowerSlideFieldKeys(
  slide: ISlide,
  fieldId: string
): string[] {
  const value = slide.fields?.[fieldId];
  if (value === undefined) return [];
  return Array.isArray(value) ? value.filter(Boolean) : value ? [value] : [];
}

export function getPowerSlideTitleKey(slide: ISlide): string {
  return getPowerSlideFieldKeys(slide, POWER_SLIDE_TITLE_FIELD)[0] ?? "";
}

/** Every localization key in the deck, in reading order, without duplicates. */
export function collectPowerSlideKeys(deck: IPowerSlide): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  deck.slides?.forEach((slide) => {
    const template = getPowerSlideTemplate(slide.templateId);
    // Fall back to whatever the slide actually stores so an unregistered
    // template still has its keys copied and translated rather than dropped.
    const fieldIds = template
      ? template.textFields.map((field) => field.id)
      : Object.keys(slide.fields ?? {});
    fieldIds.forEach((fieldId) => {
      getPowerSlideFieldKeys(slide, fieldId).forEach((key) => {
        if (seen.has(key)) return;
        seen.add(key);
        keys.push(key);
      });
    });
  });
  return keys;
}

/** Deep copy of a slide onto a fresh id, remapping every localization key. */
export function remapPowerSlide(
  slide: ISlide,
  keyMap: ReadonlyMap<string, string>
): ISlide {
  const fields: Record<string, PowerSlideFieldValue> = {};
  Object.entries(slide.fields ?? {}).forEach(([fieldId, value]) => {
    fields[fieldId] = Array.isArray(value)
      ? value.map((key) => keyMap.get(key) ?? key)
      : (keyMap.get(value) ?? value);
  });
  return {
    id: uuidv4(),
    templateId: slide.templateId,
    fields,
    assets: (slide.assets ?? []).map((asset) => ({ ...asset })),
  };
}

/**
 * The rail label for a slide. Untitled slides fall back to the template name as
 * a placeholder - there is no separate rename affordance, the title is the name.
 * The title is rich text, so its formatting is stripped: a label is a single
 * line of chrome and would otherwise read out its own markup.
 */
export function getPowerSlideLabel(
  slide: ISlide,
  getValue: (key: string) => string
): { label: string; isPlaceholder: boolean } {
  const titleKey = getPowerSlideTitleKey(slide);
  const title = titleKey ? stripFormattingTags(getValue(titleKey)).trim() : "";
  if (title) return { label: title, isPlaceholder: false };
  const template: PowerSlideTemplate | undefined = getPowerSlideTemplate(
    slide.templateId
  );
  return { label: template?.name ?? "Slide", isPlaceholder: true };
}
